import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { createSearchParams, useNavigate } from 'react-router-dom';
import AppConfig from '../../config';
import { usePageContext } from '../../hooks/usePageContext';
import { t } from '../../lang';
import HelperService from '../../services/HelperService';
import TypesService from '../../services/TypesService';
import WebSocketService from '../../services/WebSocketService';
import { appSlice } from '../../store/slices';
import Icon, { Icons } from '../Icon/Icon';
import IconAML from '../IconAML/IconAML';
import ListItem from '../ListItem';
import './AMLSummary.scss';

const { routes } = AppConfig;
const BlockchainsById = TypesService.BlockchainsById;

const risksParams = {
	Low: { title: 'Low risk' },
	Medium: { title: 'Medium risk' },
	High: { title: 'High risk' },
	Outdated: { title: 'Outdated risk' },
	Unchecked: { title: 'Unchecked risk' },
	Unverified: { title: 'Unverified' },
};

const AMLSummary = ({
	address = '',
	percent = null,
	isActualAML = true,
	blockchainName = '',
	blockchainId = null,
	showBlockchain = false,
	isError = false,
	isLoading = false,
	date = '',
	highRisk = 0,
	bottomSeparator = false,
	addressAmlId = null,
	checkDate = null,
	className,
}) => {
	const intl = useIntl();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { useSearchParams } = usePageContext();

	const currencyId = Number(useSearchParams().get('currencyId'));

	const blockchain = blockchainName
		? Object.values(BlockchainsById).find(i => i.network === blockchainName)
		: BlockchainsById[blockchainId];

	const { risk, riskStatus } = HelperService.getAmlRiskByText(
		percent,
		isActualAML,
		blockchain?.network,
		isError,
		checkDate
	);

	const onClick = () => {
		dispatch(appSlice.actions.setWsAmlResult(null));

		if (isLoading) {
			WebSocketService.RemoveCustomeHandler('handlerAML');

			const handlerAML = message => {
				if (message?.type !== 'MobileAddressAmlResultMsg') return;

				const amlResult = message?.msg?.addressAmlResult;
				if (!amlResult) return;

				if (amlResult.address !== address) return;

				dispatch(appSlice.actions.setWsAmlResult(amlResult));
			};

			WebSocketService.AddCustomHandler(handlerAML);

			return navigate({
				pathname: routes.amlCheckResult,
				search: createSearchParams({ address, blockchain: blockchain?.network }).toString(),
			});
		}

		if (isError) 
			return navigate({
				pathname: routes.amlCheckResult,
				search: createSearchParams({ openError: true, address }).toString(),
			});

		riskStatus.unchecked
			? navigate({
					pathname: routes.amlCheck,
					search: createSearchParams({ address }).toString(),
			  })
			: navigate({
					pathname: routes.addressAmlDetails,
					search: createSearchParams({ currencyId, address, addressAmlId }).toString(),
			  });
	};

	const dateSplit = date?.split('T');
	const timeArr = dateSplit?.[1]?.split('.')[0].split(':') ?? [];
	const time = timeArr.length >= 2 ? `${timeArr[0]}:${timeArr[1]}` : '';
	const [y, m, d] = dateSplit?.[0]?.split('-') ?? [];
	const dateUi = y && m && d ? `${d}.${m}.${y.slice(-2)}` : '';

	return (
		<ListItem
			className={classNames('AMLSummary', risk, className, {
				'high-risk': highRisk > 1,
				'hide-percents': isLoading || riskStatus.unchecked,
				loading: isLoading,
			})}
			onSelect={onClick}
			iconComponent={
				<IconAML
					percent={percent}
					isActualAML={isActualAML}
					blockchainName={blockchainName}
					blockchainId={blockchainId}
					isLoading={isLoading}
					showBlockchain={showBlockchain}
					checkDate={checkDate}
					isError={isError}
				/>
			}
			title={isLoading ? t('In progress V2') : risk === 'ErrorTriangle' ? t('Error') : t(risksParams[risk].title)}
			description={riskStatus.unchecked ? t('Check AML risk') : `${dateUi} ${time}`.trim()}
			rightTitle={`${percent || 0}%`}
			rightDescription={`${intl.formatMessage({ id: 'High risk' })} %: ${highRisk || 0}`}
			trailing={[riskStatus.unchecked && <Icon className='AMLSummary__arrow' type={Icons.ARROW_CHEVRON} height={16} />]}
			bottomSeparator={bottomSeparator}
		/>
	);
};

AMLSummary.propTypes = {
	percent: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
	isActualAML: PropTypes.bool,
	blockchainId: PropTypes.number,
	blockchainName: PropTypes.string,
	showBlockchain: PropTypes.bool,
	isError: PropTypes.bool,
	isLoading: PropTypes.bool,
	addressAmlId: PropTypes.number,
	address: PropTypes.string,
	date: PropTypes.string,
	highRisk: PropTypes.number,
};

export default AMLSummary;
